import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import {
  PageShell,
  Pagination,
  SegmentedFilter,
  StatusPill,
  inkA,
  tokens,
  useIsMobile,
} from 'components/v3';
import { fieldErrorsOf } from 'shared/apolloFieldErrors';
import storage from 'shared/storage';
import { useFormSubmissionsExport, useGetFormSubmissions } from '../Forms/gql';
import { formatAnswer } from '../Forms/shared/answerFormat';
import { downloadUrl } from '../Forms/shared/exporters/browser';
import useFormsToaster from '../Forms/shared/useFormsToaster';
import EntriesChart from '../Forms/Submissions/EntriesChart';
import { BarButton } from '../Forms/Submissions/entry/DownloadMenu';
import EntryModal from '../Forms/Submissions/entry/EntryModal';
import { columnsById } from '../Forms/Submissions/exportMatrix';
import { exportedToast, exportRunInput } from '../Forms/Submissions/exportRun';
import Glyph from '../Forms/Submissions/Glyph';
import PublicLinkBar from '../Forms/Submissions/PublicLinkBar';
import SubmissionsTable, {
  DEFAULT_SORT,
  nextSort,
} from '../Forms/Submissions/SubmissionsTable';
import SubmissionsToolbar from '../Forms/Submissions/SubmissionsToolbar';
import { sx } from '../Forms/Submissions/sx';
import { indexVersionFields } from '../Forms/Submissions/versionFields';
import ColumnsModal from '../Forms/Submissions/views/ColumnsModal';
import ExportModal from '../Forms/Submissions/views/ExportModal';
import {
  DEFAULT_VIEW_ID,
  catalogKeys,
  catalogOf,
  colDef,
  defaultCols,
  loadExportChoice,
  loadViews,
  newViewId,
  resolveViews,
  saveExportChoice,
  saveViews,
  w9BuiltInViews,
  w9ExportCatalogOf,
} from '../Forms/Submissions/views/viewModel';
import ViewTabs from '../Forms/Submissions/views/ViewTabs';
import AccessLog from './components/AccessLog';
import FilterMenu, {
  countsFor,
  isFilterableField,
  matchesFilter,
} from './components/FilterMenu';
import OnFileYears, { relatedEntries } from './components/OnFileYears';
import PersonSheet from './components/PersonSheet';
import QrSheet from './components/QrSheet';
import {
  PageHead,
  SectionBody,
  SectionHeader,
} from './components/SectionShell';
import W9Stats from './components/W9Stats';
import YearChips from './components/YearChips';
import {
  useGetW9Pdf,
  useGetW9Stats,
  useGetW9Years,
  useRevealFormSecret,
} from './gql';
import LinkPage from './LinkPage';
import SettingsPage from './SettingsPage';
import { payeeFieldsOf } from './shared/payeeFields';
import { w9DashboardPath } from './shared/routes';
import {
  MONTHS,
  currentTaxYear,
  etParts,
  etShortDate,
  keepThrough,
  resolveYear,
} from './shared/taxYear';
import useW9Accounts from './shared/useW9Accounts';
import useW9Section from './shared/useW9Section';
import { NAME_FIELD_ID, payeeFieldIds } from './shared/w9Template';

// Settings → W-9s (w9-plan §6.3; design w9_app.jsx W9Dashboard): one
// account's W-9 collection, scoped to a tax year (or every year at once).
// The query string is the state: ?account=<id>&year=<yyyy|all>
// &filter=notback&entry=<id>. Everything under the year chips is the
// Forms submissions page's own parts (T15): the link bar, the chart, the
// toolbar, the saved-view table, the Columns / Export dialogs and the
// entry card, each with its W-9 slot filled.

export const PAGE_SIZE = 25;

const signedInUserId = () => {
  const user = storage.get('user', {});
  return (user && user.id) || null;
};

// Definitions newest first (the working copy, then every version the
// list returned), for grouping columns by section.
const definitionsOf = (form, versions) => [
  ...(form && form.working_version && form.working_version.definition
    ? [form.working_version.definition]
    : []),
  ...[...(versions || [])]
    .sort((a, b) => (b.version_number || 0) - (a.version_number || 0))
    .map((v) => v.definition),
];

// The list inputs that pick the W-9 rows (w9-plan T6, §3.2).
export const w9ListFilters = ({ year, filter }) =>
  year === 'all'
    ? { payee_latest: true }
    : {
        tax_year: year,
        ...(filter === 'notback' ? { not_back: true } : {}),
      };

export const emptyBodyFor = ({ all, filter, archived, year }) => {
  if (filter === 'notback') {
    return `Everyone from ${
      year - 1
    } is back: every driver who filed then has a ${year} W-9 on file.`;
  }
  if (all) return 'Nobody has signed a W-9 for this account yet.';
  if (archived) return `Nothing was filed under ${year}.`;
  return `Share your collection link. W-9s file under ${year} as drivers sign them.`;
};

export const rowName = (row) =>
  (row && row.answers && String(row.answers[NAME_FIELD_ID] || '').trim()) ||
  (row && row.submitter_name) ||
  'Driver';

// The first phone-type payee answer on the row, formatted.
export const rowPhone = (row, payeeFields) => {
  const field = payeeFields.find((f) => f.type === 'phone');
  if (!field || !row || !row.answers) return null;
  return formatAnswer(field, row.answers[field.id]) || null;
};

// Last settled list, so a new search or page keeps showing the previous
// rows until the server answers. A failed load forgets them.
const useSettledList = (list) => {
  const last = useRef(null);
  const payload =
    !list.error && list.data ? list.data.getFormSubmissions : null;
  const current = useMemo(
    () =>
      payload
        ? {
            rows: payload.results || [],
            count: payload.count || 0,
            columns: payload.columns || [],
            versions: payload.versions || [],
          }
        : null,
    [payload]
  );
  if (list.error) last.current = null;
  if (current) last.current = current;
  return current || last.current;
};

const listErrorOf = (list) =>
  list.error && !list.loading ? fieldErrorsOf(list.error).global : null;

const retry = (list) => () => {
  if (typeof list.refetch !== 'function') return;
  Promise.resolve(list.refetch()).catch(() => undefined);
};

const ArchivedNote = ({ year, current, onGo }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        padding: '12px 12px 12px 16px',
        borderRadius: 12,
        border: '1px solid ' + t.border,
        background: inkA(theme, 0.05),
        fontSize: 13.5,
        lineHeight: 1.45,
        color: t.sub,
      }}
    >
      <Glyph name="archive" size={18} />
      <span style={{ flex: '1 1 240px', minWidth: 0 }}>
        <strong style={{ color: t.text }}>{year} is archived.</strong> These
        W-9s are read-only and kept through {keepThrough(year)}, per IRS
        record-keeping guidance.
      </span>
      <ButtonV2 onClick={onGo} style={{ width: 'auto' }}>
        Go to {current}
      </ButtonV2>
    </div>
  );
};

ArchivedNote.propTypes = {
  year: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  onGo: PropTypes.func.isRequired,
};

// Stats, chart, toolbar, table and the entry card for one year (or all).
// Keyed by year + filter, so a switch starts fresh (search, sort, page).
const Entries = ({
  form,
  years,
  selected,
  currentYear,
  hasPrior,
  filter,
  stats,
  statsLoading,
  params,
  routePrefix,
  go,
  showToast,
  isMobile,
  payeeFields,
}) => {
  const history = useHistory();
  const all = selected === 'all';
  const archived = !all && selected < currentYear;
  const isCurrent = !all && !archived;
  const userId = useMemo(signedInUserId, []);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState(null); // 'cols' | 'new' | 'export'
  const [storedViews, setStoredViews] = useState(() =>
    loadViews(form.id, userId)
  );
  const [lastExport, setLastExport] = useState(() =>
    loadExportChoice(form.id, userId)
  );
  const [localFilters, setLocalFilters] = useState({});
  const [allChip, setAllChip] = useState('all');
  const [person, setPerson] = useState(null);
  const [logVersion, setLogVersion] = useState(0);
  const [pdfBusy, setPdfBusy] = useState(false);
  const pendingNav = useRef(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const w9Filters = w9ListFilters({ year: selected, filter });
  const list = useGetFormSubmissions(
    {
      form_id: form.id,
      ...(search ? { search } : {}),
      sort: sort.key,
      direction: sort.direction,
      page,
      limit: PAGE_SIZE,
      ...w9Filters,
    },
    { fetchPolicy: 'cache-and-network' }
  );
  const settled = useSettledList(list);
  const exportSubmissions = useFormSubmissionsExport();
  const getW9Pdf = useGetW9Pdf();
  const reveal = useRevealFormSecret();

  const rows = settled ? settled.rows : [];
  const count = settled ? settled.count : 0;
  const serverColumns = settled ? settled.columns : null;
  const versionIndex = useMemo(
    () => indexVersionFields(settled && settled.versions),
    [settled]
  );
  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));

  // Every driver (All years, no search): the stats' "Drivers on file".
  const driversRef = useRef(null);
  if (all && settled && !search) driversRef.current = count;
  const stored = years.reduce((n, y) => n + (y.count || 0), 0);
  const allStats = {
    drivers: driversRef.current || 0,
    current: stats ? stats.on_file : 0,
    earlier:
      driversRef.current != null && stats
        ? Math.max(0, driversRef.current - stats.on_file)
        : 0,
    stored,
    yearCount: years.length,
  };

  // ---- client-side narrowing (payee filters; the All-years status chips) ----
  const filterFields = useMemo(
    () => payeeFields.filter(isFilterableField),
    [payeeFields]
  );
  const visibleRows = useMemo(
    () =>
      rows.filter((row) => {
        if (all && allChip !== 'all') {
          const current = row.tax_year === currentYear;
          if ((allChip === 'current') !== current) return false;
        }
        return filterFields.every((f) =>
          matchesFilter(
            f,
            row.answers && row.answers[f.id],
            localFilters[f.id] || ''
          )
        );
      }),
    [rows, all, allChip, currentYear, filterFields, localFilters]
  );
  const narrowed =
    !!search ||
    Object.values(localFilters).some(Boolean) ||
    (all && allChip !== 'all');

  // ---- saved views ----
  const definitions = useMemo(
    () => definitionsOf(form, settled && settled.versions),
    [form, settled]
  );
  const catalog = useMemo(
    () => catalogOf(serverColumns, definitions),
    [serverColumns, definitions]
  );
  const exportCatalog = useMemo(
    () => w9ExportCatalogOf(serverColumns, definitions),
    [serverColumns, definitions]
  );
  const keys = useMemo(
    () => (serverColumns ? catalogKeys(catalog) : []),
    [catalog, serverColumns]
  );
  const exportKeys = useMemo(
    () => (serverColumns ? catalogKeys(exportCatalog) : []),
    [exportCatalog, serverColumns]
  );
  const defaults = useMemo(() => defaultCols(serverColumns), [serverColumns]);
  const builtIns = useMemo(
    () => w9BuiltInViews(serverColumns),
    [serverColumns]
  );
  const vs = useMemo(
    () => resolveViews(storedViews, keys, defaults, builtIns),
    [storedViews, keys, defaults, builtIns]
  );
  const activeView = vs.views.find((v) => v.id === vs.active) || vs.views[0];
  const tableColumns = useMemo(() => {
    const byId = columnsById(serverColumns);
    const cols = activeView.cols.map((k) => colDef(k, byId)).filter(Boolean);
    return cols.length
      ? cols
      : defaults.map((k) => colDef(k, byId)).filter(Boolean);
  }, [activeView, serverColumns, defaults]);

  const updateViews = (change) => {
    const next = change(vs);
    setStoredViews(next);
    saveViews(form.id, userId, next);
  };

  const onSaveColumns = ({ name, cols }) => {
    if (dialog === 'new') {
      const idForView = newViewId();
      updateViews((cur) => ({
        active: idForView,
        views: cur.views.concat({ id: idForView, name, cols }),
      }));
      showToast(`View “${name}” created`);
    } else {
      updateViews((cur) => ({
        ...cur,
        views: cur.views.map((v) =>
          v.id === activeView.id ? { ...v, name, cols } : v
        ),
      }));
      showToast(`View “${name}” saved`);
    }
    setDialog(null);
  };

  const onDeleteView = () => {
    updateViews((cur) => ({
      active: DEFAULT_VIEW_ID,
      views: cur.views.filter((v) => v.id !== activeView.id),
    }));
    showToast(`View “${activeView.name}” deleted`);
    setDialog(null);
  };

  const onSaveViewFromExport = (name, cols) => {
    const idForView = newViewId();
    updateViews((cur) => ({
      ...cur,
      views: cur.views.concat({ id: idForView, name, cols }),
    }));
    showToast(`View “${name}” saved · find it above the table`);
  };

  // ---- export (the worker builds the file; the W-9 filters ride along) ----
  const onRunExport = async ({ fmt, cols, scope }) => {
    const choice = { fmt, cols };
    saveExportChoice(form.id, userId, choice);
    setLastExport(choice);
    const { data, error } = await exportSubmissions({
      ...exportRunInput({ formId: form.id, fmt, cols, scope, search, sort }),
      ...w9Filters,
    });
    if (!mounted.current) return false;
    if (error) {
      showToast({ type: 'error', title: error.global });
      return false;
    }
    downloadUrl(data.url, data.run.filename);
    showToast(exportedToast(fmt, data.run.rows));
    return true;
  };

  // ---- the W-9 PDF (T12): a signed link, logged on the server ----
  const openPdf = async (entry) => {
    if (pdfBusy) return;
    setPdfBusy(true);
    const res = await getW9Pdf(entry.id);
    if (!mounted.current) return;
    setPdfBusy(false);
    if (res.error) {
      showToast({ type: 'error', title: res.error.global });
      return;
    }
    downloadUrl(res.url, res.filename);
    setLogVersion((v) => v + 1);
  };

  // The reveal (T11), then the access log picks up the new event.
  const revealSecret = useCallback(
    async (submissionId, fieldId) => {
      const res = await reveal(submissionId, fieldId);
      if (res && !res.error && mounted.current) setLogVersion((v) => v + 1);
      return res;
    },
    [reveal]
  );

  // ---- entries ----
  const entryId = params.entry;
  const personOf = (row) => ({
    id: row.id,
    name: rowName(row),
    year: row.tax_year || selected - 1,
    phone: rowPhone(row, payeeFields),
    email: row.submitter_email || null,
  });
  const openEntry = (row) => {
    if (filter === 'notback') {
      setPerson(personOf(row));
      return;
    }
    go({ entry: row.id }, { replace: true });
  };
  const closeEntry = () => go({ entry: null }, { replace: true });

  useEffect(() => {
    if (!pendingNav.current || list.loading || !settled) return;
    const target =
      pendingNav.current === 'first'
        ? settled.rows[0]
        : settled.rows[settled.rows.length - 1];
    pendingNav.current = null;
    if (target) go({ entry: target.id }, { replace: true });
  }, [settled, list.loading, go]);

  const at = visibleRows.findIndex((r) => r.id === entryId);
  const nav =
    at >= 0
      ? {
          index: (page - 1) * PAGE_SIZE + at,
          total: count,
          onPrev: () => {
            if (at > 0) openEntry(visibleRows[at - 1]);
            else if (page > 1) {
              pendingNav.current = 'last';
              setPage(page - 1);
            }
          },
          onNext: () => {
            if (at < visibleRows.length - 1) openEntry(visibleRows[at + 1]);
            else if (page < pageCount) {
              pendingNav.current = 'first';
              setPage(page + 1);
            }
          },
        }
      : null;

  // Only payee details are editable, and only on the current year (D4).
  const entryRow = rows.find((r) => r.id === entryId) || null;
  const entryYear =
    entryRow && entryRow.tax_year ? entryRow.tax_year : selected;
  const editableFieldIds =
    entryYear === 'all' || entryYear === currentYear
      ? payeeFieldIds(payeeFields)
      : [];

  const extraActions = (submission) => (
    <BarButton
      onClick={() => openPdf(submission)}
      disabled={pdfBusy}
      data-w9-pdf=""
    >
      <Glyph name="file-text" size={15} />
      {pdfBusy ? 'Preparing…' : 'W-9 form'}
    </BarButton>
  );
  const downloadItems = (submission) => [
    {
      key: 'w9pdf',
      icon: 'file-text',
      label: 'IRS Form W-9 (PDF)',
      sub: 'The official form, filled in',
      run: () => openPdf(submission),
    },
  ];
  const extraSections = (submission) => {
    const related = relatedEntries(rows, submission);
    return (
      <>
        {related.length > 1 ? (
          <OnFileYears
            entries={related}
            activeId={submission.id}
            currentYear={currentYear}
            onPdf={openPdf}
            isMobile={isMobile}
          />
        ) : null}
        <AccessLog
          submissionId={submission.id}
          submitterName={rowName(submission)}
          refreshKey={logVersion}
          isMobile={isMobile}
        />
      </>
    );
  };

  const onSearch = (text) => {
    setSearch(text.trim());
    setPage(1);
  };
  const onSort = (key) => {
    setSort((cur) => nextSort(cur, key));
    setPage(1);
  };

  // ---- chart (current year: the months still ahead are dimmed) ----
  const et = etParts();
  const live = isCurrent && selected === et.year;
  const byMonth = (stats && stats.by_month) || [];
  const total = byMonth.reduce((n, v) => n + (v || 0), 0);
  const busiest = total ? MONTHS[byMonth.indexOf(Math.max(...byMonth))] : null;

  // ---- status chips ----
  let chips = null;
  if (!all && isCurrent && hasPrior) {
    chips = (
      <SegmentedFilter
        value={filter || 'all'}
        options={[
          { key: 'all', label: 'On file', count: stats ? stats.on_file : null },
          {
            key: 'notback',
            label: 'Not back',
            count: stats ? stats.not_back : null,
          },
        ]}
        onChange={(k) =>
          go({ filter: k === 'notback' ? 'notback' : null, entry: null })
        }
      />
    );
  } else if (all) {
    const current = rows.filter((r) => r.tax_year === currentYear).length;
    chips = (
      <SegmentedFilter
        value={allChip}
        options={[
          { key: 'all', label: 'All drivers', count: rows.length },
          { key: 'current', label: 'Current', count: current },
          {
            key: 'earlier',
            label: 'Earlier year',
            count: rows.length - current,
          },
        ]}
        onChange={setAllChip}
      />
    );
  }

  return (
    <>
      <W9Stats
        mode={all ? 'all' : archived ? 'archived' : 'current'}
        year={all ? currentYear : selected}
        stats={stats}
        loading={statsLoading}
        hasPrior={hasPrior}
        allStats={allStats}
        activeFilter={filter}
        onToggleNotBack={() =>
          go({ filter: filter === 'notback' ? null : 'notback', entry: null })
        }
        isMobile={isMobile}
      />
      {all ? null : (
        <EntriesChart
          buckets={stats ? byMonth : []}
          labels={MONTHS}
          dimFrom={live ? et.month + 1 : null}
          currentIndex={live ? et.month : -1}
          title="W-9s by month"
          summary={`${total.toLocaleString()} signed in ${selected}${
            busiest ? ` · busiest month ${busiest}` : ''
          }`}
          range={`Jan – Dec ${selected}`}
          note={live ? `Through ${etShortDate()}` : 'Full tax year'}
          loading={statsLoading}
          isMobile={isMobile}
        />
      )}
      <SubmissionsToolbar
        count={narrowed && !search ? visibleRows.length : count}
        counting={!settled}
        onSearch={onSearch}
        onExport={() => setDialog('export')}
        canExport={!!settled && count > 0}
        isMobile={isMobile}
        searchPlaceholder="Search name, business, car #…"
      >
        {chips}
        {filterFields.map((field) => (
          <FilterMenu
            key={field.id}
            field={field}
            value={localFilters[field.id] || ''}
            counts={countsFor(field, rows)}
            onChange={(value) =>
              setLocalFilters((cur) => ({ ...cur, [field.id]: value }))
            }
          />
        ))}
      </SubmissionsToolbar>
      <SubmissionsTable
        columns={tableColumns}
        versionIndex={versionIndex}
        rows={visibleRows}
        sort={sort}
        onSort={onSort}
        onOpen={openEntry}
        activeId={entryId}
        loading={!!list.loading}
        searching={narrowed}
        emptyBody={emptyBodyFor({ all, filter, archived, year: selected })}
        error={listErrorOf(list)}
        onRetry={retry(list)}
        header={
          <ViewTabs
            views={vs.views}
            activeId={vs.active}
            columnCount={tableColumns.length}
            onPick={(viewId) =>
              updateViews((cur) => ({ ...cur, active: viewId }))
            }
            onNew={() => setDialog('new')}
            onColumns={() => setDialog('cols')}
          />
        }
        footer={
          pageCount > 1 ? (
            <Pagination page={page} pageCount={pageCount} onChange={setPage} />
          ) : null
        }
      />

      <EntryModal
        entryId={filter === 'notback' ? null : entryId}
        preview={entryRow}
        form={form}
        nav={nav}
        isMobile={isMobile}
        onClose={closeEntry}
        showToast={showToast}
        extraActions={extraActions}
        extraSections={extraSections}
        downloadItems={downloadItems}
        editableFieldIds={editableFieldIds}
        revealSecret={revealSecret}
      />
      <PersonSheet
        open={!!person}
        person={person}
        currentYear={currentYear}
        url={form.w9_url || ''}
        onClose={() => setPerson(null)}
        onViewEntry={(p) =>
          history.push(
            w9DashboardPath(routePrefix, {
              account: form.account.id,
              year: p.year,
              entry: p.id,
            })
          )
        }
        onToast={showToast}
      />
      {dialog === 'cols' || dialog === 'new' ? (
        <ColumnsModal
          catalog={catalog}
          isNew={dialog === 'new'}
          view={
            dialog === 'new'
              ? { id: null, name: '', cols: activeView.cols }
              : activeView
          }
          defaults={defaults}
          onSave={onSaveColumns}
          onDelete={onDeleteView}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'export' ? (
        <ExportModal
          catalog={exportCatalog}
          keys={exportKeys}
          views={vs.views}
          activeId={vs.active}
          formName={all ? 'W-9s · all years' : `${selected} W-9s`}
          lastChoice={lastExport}
          searching={!!search}
          matchingCount={count}
          allCount={all ? allStats.drivers : stats ? stats.on_file : count}
          onRun={onRunExport}
          onSaveView={onSaveViewFromExport}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
};

Entries.propTypes = {
  form: PropTypes.object.isRequired,
  years: PropTypes.arrayOf(PropTypes.object).isRequired,
  selected: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['all'])])
    .isRequired,
  currentYear: PropTypes.number.isRequired,
  hasPrior: PropTypes.bool.isRequired,
  filter: PropTypes.string,
  stats: PropTypes.object,
  statsLoading: PropTypes.bool.isRequired,
  params: PropTypes.object.isRequired,
  routePrefix: PropTypes.string.isRequired,
  go: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  payeeFields: PropTypes.arrayOf(PropTypes.object).isRequired,
};

Entries.defaultProps = { filter: null, stats: null };

const DashboardBody = ({ form, params, routePrefix, showToast }) => {
  const history = useHistory();
  const theme = useTheme();
  const t = tokens(theme);
  const s = sx(theme);
  const isMobile = useIsMobile();
  const accountId = form.account.id;
  const { years } = useGetW9Years(form.id);
  const currentYear =
    (years.find((y) => y.status === 'current') || {}).year || currentTaxYear();
  const selected = resolveYear(params.year, currentYear);
  const all = selected === 'all';
  const archived = !all && selected < currentYear;
  const isCurrent = !all && !archived;
  const hasPrior = !all && years.some((y) => y.year === selected - 1);
  const filter =
    params.filter === 'notback' && isCurrent && hasPrior ? 'notback' : null;
  const statsQuery = useGetW9Stats(form.id, all ? currentYear : selected);
  const stats = statsQuery.stats;
  const statsLoading = !stats && !!statsQuery.loading;
  const [qrOpen, setQrOpen] = useState(false);
  const payeeFields = useMemo(() => payeeFieldsOf(form), [form]);

  const go = useCallback(
    (patch, { replace = false } = {}) => {
      const path = w9DashboardPath(routePrefix, {
        account: accountId,
        year: params.year,
        filter: params.filter,
        entry: params.entry,
        ...patch,
      });
      if (replace) history.replace(path);
      else history.push(path);
    },
    [history, routePrefix, accountId, params.year, params.filter, params.entry]
  );
  const pickYear = (year) => go({ year, filter: null, entry: null });

  const yearList = years.map((y) => y.year);
  const span = yearList.length
    ? `${Math.min(...yearList)}–${Math.max(...yearList)}`
    : String(currentYear);
  const badge = isCurrent ? (
    <StatusPill
      label="Collecting"
      color={s.ok}
      background={s.okBg}
      border={s.okBg}
    />
  ) : (
    <StatusPill
      label="Archived"
      color={t.sub}
      background={s.fill}
      border={s.line2}
    />
  );

  return (
    <div style={{ display: 'grid', gap: isMobile ? 14 : 16 }}>
      {all ? (
        <PageHead
          title="All tax years"
          sub={`${form.account.name} · ${span} · one row per driver, showing their latest W-9`}
        />
      ) : (
        <PageHead
          title={`${selected} tax year`}
          badge={badge}
          sub={`${form.account.name} · ${
            isCurrent ? `W-9s signed in ${selected} file here` : 'read-only'
          }`}
        />
      )}
      <YearChips years={years} active={selected} onPick={pickYear} />
      {archived ? (
        <ArchivedNote
          year={selected}
          current={currentYear}
          onGo={() => pickYear(currentYear)}
        />
      ) : (
        <PublicLinkBar
          form={form}
          url={form.w9_url || undefined}
          label="W-9 collection link · files by the year it's signed"
          onToast={showToast}
          extraActions={
            form.w9_url ? (
              <ButtonV2
                onClick={() => setQrOpen(true)}
                icon={<Glyph name="grid-3x3" size={15} />}
                style={{
                  width: 'auto',
                  flex: isMobile ? '1 1 0' : '0 0 auto',
                  whiteSpace: 'nowrap',
                }}
              >
                QR code
              </ButtonV2>
            ) : null
          }
        />
      )}
      <Entries
        key={`${selected}:${filter || ''}`}
        form={form}
        years={years}
        selected={selected}
        currentYear={currentYear}
        hasPrior={hasPrior}
        filter={filter}
        stats={stats}
        statsLoading={statsLoading}
        params={params}
        routePrefix={routePrefix}
        go={go}
        showToast={showToast}
        isMobile={isMobile}
        payeeFields={payeeFields}
      />
      {form.w9_url ? (
        <QrSheet
          open={qrOpen}
          url={form.w9_url}
          slug={form.slug}
          accountName={form.account.name}
          onClose={() => setQrOpen(false)}
          onToast={showToast}
        />
      ) : null}
    </div>
  );
};

DashboardBody.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string,
    w9_url: PropTypes.string,
    account: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    working_version: PropTypes.object,
  }).isRequired,
  params: PropTypes.object.isRequired,
  routePrefix: PropTypes.string.isRequired,
  showToast: PropTypes.func.isRequired,
};

const W9Dashboard = () => {
  const section = useW9Section(w9DashboardPath);
  const { accounts, loading: accountsLoading } = useW9Accounts();
  const { showToast, toasterNode } = useFormsToaster();
  return (
    <PageShell>
      <SectionHeader
        page="dashboard"
        routePrefix={section.routePrefix}
        accountId={section.accountId}
        accounts={accounts}
        accountsLoading={accountsLoading}
        onPickAccount={section.pickAccount}
      />
      <SectionBody
        accountId={section.accountId}
        form={section.form}
        loading={section.loading}
        error={section.error}
        onRetry={section.retry}
      >
        {(form) => (
          <DashboardBody
            key={form.id}
            form={form}
            params={section.params}
            routePrefix={section.routePrefix}
            showToast={showToast}
          />
        )}
      </SectionBody>
      {toasterNode}
    </PageShell>
  );
};

export const W9LinkPage = LinkPage;
export const W9SettingsPage = SettingsPage;

export default W9Dashboard;
